/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { EventoDataType } from "@/types/events"
import { PessoaDisponivelDataType } from "@/types/company"
import { getPessoasDisponiveis } from "@/services/companyService"
import { useLoading } from "@/hooks/useLoading"
import { Input } from "../../../components/ui/input"
import { batchSubscribeEvents } from "@/modules/Events/service/EventsService"
import { useToast } from "@/contexts/ToastContext"

interface BatchSubscribeModalProps {
  isOpen: boolean
  onBatchSubscribe: () => void
  event: EventoDataType
  onClose: () => void
}

export function BatchSubscribeModal({
  isOpen,
  event,
  onClose,
  onBatchSubscribe,
}: BatchSubscribeModalProps) {
  const [members, setMembers] = useState<PessoaDisponivelDataType[]>([])
  const [rowSelection, setRowSelection] = useState<Record<number, boolean>>({})
  const [searchQuery, setSearchQuery] = useState("")
  const toast = useToast()

  const { user } = useAuth()
  const { showLoading, hideLoading } = useLoading()

  useEffect(() => {
    if (isOpen && user) {
      showLoading()
      getPessoasDisponiveis(user.empId, {
        origem: event.origem,
        id_origem: String(event.id),
        originador_id: user.pesId,
      })
        .then(({ data }) => {
          setMembers(data.data)
        })
        .catch((error) => {
          console.error("Erro ao buscar membros:", error)
        })
        .finally(() => {
          hideLoading()
        })
    }
  }, [isOpen])

  const filteredMembers = members.filter(
    (member) =>
      member.pesNome.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.pesEmail.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const toggleMemberSelection = (pesId: number) => {
    setRowSelection((prev) => {
      const newSelection = { ...prev }
      if (newSelection[pesId]) {
        delete newSelection[pesId]
      } else {
        newSelection[pesId] = true
      }
      return newSelection
    })
  }

  const handleSubscribe = async () => {
    const selectedIds = Object.keys(rowSelection).map(Number)
    const selectedMembers = members.filter((m) => selectedIds.includes(m.pesId))

    const payload = {
      pessoas: selectedMembers.map((m) => m.pesUid),
      id_produto: event.id,
      origem: event.origem,
      turma: event.turma ?? null,
    }

    await batchSubscribeEvents(payload)
      .then(() => {
        toast.success("Inscrições realizadas com sucesso!")
        onClose()
        onBatchSubscribe()
      })
      .catch((error) => {
        toast.danger(error.response.data.message)
      })
  }

  useEffect(() => {
    if (!isOpen) {
      setRowSelection({})
      setSearchQuery("")
    }
  }, [isOpen])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onClose}
    >
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex flex-col gap-2">
            <span className="font-light">Inscrição de colaboradores no evento:</span>
            <div>{event.descricao}</div>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <Input
            placeholder="Pesquisar por nome ou e-mail..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="md:col-span-2"
          />

          <div>{filteredMembers.length} colaboradores encontrados</div>

          <section className="flex max-h-96 w-full flex-col gap-4 overflow-auto p-4">
            {filteredMembers.length > 0 ? (
              filteredMembers.map((member) => {
                const isSelected = !!rowSelection[member.pesId]

                return (
                  <div
                    key={member.pesId}
                    onClick={() => toggleMemberSelection(member.pesId)}
                    className={`flex cursor-pointer items-center gap-4 rounded-lg border p-4 transition-colors ${
                      isSelected ? "border-primary bg-primary/10" : "hover:bg-accent"
                    }`}
                  >
                    <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border">
                      {!member.pesFotoUrl ? (
                        <div>
                          {member.pesNome
                            .split(" ")
                            .filter(
                              (n) => !["de", "do", "da", "dos", "das"].includes(n.toLowerCase())
                            )
                            .slice(0, 2)
                            .map((n) => n[0]?.toUpperCase())
                            .join("")}
                        </div>
                      ) : (
                        <img
                          className="object-cover"
                          src={member.pesFotoUrl}
                          alt={member.pesNome}
                        />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold">{member.pesNome}</h3>
                      <p className="text-sm text-muted-foreground">{member.cargo}</p>
                    </div>
                  </div>
                )
              })
            ) : (
              <p className="py-10 text-center text-sm text-muted-foreground">
                Nenhum membro disponível para inscrição.
              </p>
            )}
          </section>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubscribe}
            disabled={Object.keys(rowSelection).length === 0}
          >
            Inscrever {Object.keys(rowSelection).length} selecionados
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
